import classNames from 'classnames';

import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints } from '../DataPoints';
import { ProposalDAOInterface } from '../ProposalDAO';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

import { CurrencyCell, TextCell } from '@/views/components/cells';
import { HoldingChangelogItem, PortfolioAdjustment } from '@capintel/instruments-lib';

enum ChangeLogColumn {
  NAME = 'name',
  PORTFOLIO_ADJUSTMENT = 'portfolioAdjustment',
  VALUE_CHANGE = 'valueChange',
}

type FieldValue = number | string | { value: number };

/**
 * Tax Analysis Portfolio Change Log DAO
 */
export default class TaxAnalysisPortfolioChangeLogDAO extends ComparisonItemBasedProposalDAO implements ProposalDAOInterface {
  i18nBasePath = 'Proposal.Modules.TaxAnalysis.ChangeLog';

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    // Filter to only include the original portfolio item before parent constructor runs
    const originalItemId = proposal.taxAnalysis?.originalComparisonItemId;

    if (originalItemId) {
      const filteredProposal = {
        ...proposal,
        items: proposal.items.filter(item => item._id === originalItemId),
      };
      super(filteredProposal, proposalElement);
    } else {
      super(proposal, proposalElement);
    }
  }

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new TaxAnalysisPortfolioChangeLogDAO(proposal, proposalElement);
  }

  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const allItems = this.getItemDAOs();

    if (allItems.length === 0) {
      return;
    }

    const itemWithTaxData = allItems[0];
    const taxData = DataPoints.getTaxAnalysisMetrics(itemWithTaxData.item);

    if (!taxData.response?.changelog) {
      return;
    }

    const headerClasses = classNames(ProposalStyleClassNames.ProposalTableContent, 'tax-section-header');
    const cellClasses = classNames(ProposalStyleClassNames.ProposalTableContent, 'tax-section');

    // Columns header definition
    this.addFieldName(this.formatMessage(`${this.i18nBasePath}.Headers.Ticker`), headerClasses);
    const renderHeaderConfig = this.headerConfig();
    for (const [index, header] of renderHeaderConfig.entries()) {
      this.addItemField(itemWithTaxData, header.label, header.value, index + 1);
    }

    // Body config
    for (const holdingChangelogItem of taxData.response.changelog) {
      this.addFieldName(holdingChangelogItem?.symbol ?? '', cellClasses);
      const renderCellConfig = this.cellConfig(holdingChangelogItem);
      for (const [index, cell] of renderCellConfig.entries()) {
        this.addItemField(itemWithTaxData, cell.label, cell.value, index + 1, cell.cellType);
      }
    }
  }

  private addItemField(itemDAO: ComparisonItemDAO, name: string, value: FieldValue, index: number, cellType?: React.FC<any>) {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value, ProposalStyleClassNames.ProposalTableContent, cellType);
    this.addFieldDAO(itemDAO, field, index);
    return value;
  }

  cellConfig = (holdingChangelogItem: HoldingChangelogItem) => {
    const { adjustment, name, valueChange } = holdingChangelogItem;
    return [
      {
        cellType: TextCell,
        label: ChangeLogColumn.NAME,
        value: name ? this.formatLocalizedString(name) : '',
      },
      {
        cellType: TextCell,
        label: ChangeLogColumn.PORTFOLIO_ADJUSTMENT,
        value: adjustment ? this.formatMessage(`${this.i18nBasePath}.${PortfolioAdjustment[adjustment]}`) : '',
      },
      {
        cellType: CurrencyCell,
        label: ChangeLogColumn.VALUE_CHANGE,
        value: valueChange !== undefined ? { value: valueChange } : '',
      },
    ];
  };

  headerConfig = () => [
    {
      label: ChangeLogColumn.NAME,
      value: this.formatMessage(`${this.i18nBasePath}.Headers.Name`),
    },
    {
      label: ChangeLogColumn.PORTFOLIO_ADJUSTMENT,
      value: this.formatMessage(`${this.i18nBasePath}.Headers.PortfolioAdjustment`),
    },
    {
      label: ChangeLogColumn.VALUE_CHANGE,
      value: this.formatMessage(`${this.i18nBasePath}.Headers.ValueChange`),
    },
  ];
}
